import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '@umanforge/common';

import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';

import BaseModal from '../base_modal.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import { dayjs } from '@umanforge/common';
import { Location } from 'interfaces/location.interface';
import { LocationService } from 'services/location.service';
import BottomModal from '@common_components/modals/bottomModal.component';
import InputText from '@common_components/inputs/textInput.component';

interface LocationModalProps {
  onLocationCreate?: ( location: Location ) => void | undefined;
  onUpdate?: ( location: Location ) => void | undefined;
  onDeleteLocation?: ( locationid: string ) => void | undefined;
  onClose: () => void;
  planningId: string;
  sectorId: string;
  tableNameProps?: string;
  locationId?: string;
  timeStartDrag?: dayjs.Dayjs;
  timeEndDrag?: dayjs.Dayjs;
  dateDrag?: dayjs.Dayjs;
}

interface LocationErrors {
  name: string | undefined;
}

const LocationModal: React.FC<LocationModalProps> = ( {
  onLocationCreate,
  onDeleteLocation,
  onClose,
  onUpdate,
  locationId,
  sectorId
} ) => {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ isFormValid, setIsFormValid ] = useState( false );
  const [ isFormChanged, setIsFormChanged ] = useState( false );
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ location, setLocation ] = useState<Location>( );
  const [ name, setName ] = useState<string>();

  const [ errors, setErrors ] = useState<LocationErrors>( { name: undefined } );

  const handleCloseModal = () => {
    onClose();
  };

  // Fetch data functions
  const fetchTask = async () => {
    if ( !locationId ) {return;}

    try {
      const _location = await LocationService.getLocationById( locationId, token, dispatch );
      setLocation( _location );
      setName( _location.name );
    }
    catch ( error ) {
      console.error( 'Error fetching location:', error );
    }
  };


  // Use Effects
  useEffect( () => {
    if ( !isFirstRender && locationId ) {
      fetchTask();
    }
  }, [ isFirstRender ] );

  useEffect( () => {
    setIsFirstRender( false );
  }, [ ] );

  useEffect( () => {
    const isValid = validateForm();

    const isChanged = locationId ? (
      location?.name != name
    ) : (
      ( name && name.trim() !== '' )
    );

    setIsFormValid( isValid && Boolean( isChanged ) );
    setIsFormChanged( Boolean( isChanged ) );
  }, [ name, location, locationId ] );

  const validateForm = (): boolean => {
    const newErrors: LocationErrors = { name: undefined };
    const regex = new RegExp( /^[^,\/]+$/ );

    if ( name === '' || name == null ) {
      return false;
    }
    else if ( name.length < 3 ) {
      newErrors.name = t( 'translation:location_name_too_short' );
    }
    else if ( name.length > 255 ) {
      newErrors.name = t( 'translation:location_name_too_long' );
    }
    else if ( !regex.test( name ) ) {
      newErrors.name = t( 'translation:location_name_contains_invalid_characters' );
    }
    setErrors( newErrors );
    return ( !newErrors.name );
  };

  // Handle function
  const handleSubmit = async () => {
    if ( !isFormValid ) {
      if ( !isFormChanged ) {
        return; // TODO toast
      }
      else {
        return; // TODO toast
      }
    }

    const base: Location = {
      id: locationId ?? '',
      name: name!,
      idSector: sectorId
    };

    if ( onLocationCreate && !locationId ) {
      const newTaskId = await onLocationCreate( base );
      if ( newTaskId ) {
        handleCloseModal();
      }
    }

    if ( onUpdate && locationId ) {
      onUpdate(  base  );
    }
  };

  const handleDelete = () => {
    if ( locationId && onDeleteLocation ) {
      onDeleteLocation( locationId );
    }
    handleCloseModal();
  };


  // Render
  return (
    <>
      <BaseModal
        isOpen={true}
        onClose={handleCloseModal}
        noConfirmationModal={!isFormChanged}
        icon={locationId ? <EditBoxIcon className="bg-blue-200 p-2 rounded-md" size={10} /> : <PlusIcon className="bg-green-200 p-2 rounded-md" size={10} />}
        title={locationId ? t( 'translation:edit_location' ) : t( 'translation:create_location' )}
        footer={
          <BottomModal
            tableId={locationId}
            formValid={isFormValid}
            onCreate={handleSubmit}
            onUpdate={handleSubmit}
            onDelete={() => {return setIsDeleteModalOpen( true );}}
          />
        }
      >
        <div className="py-5 px-7 gap-y-4 bg-white font-texte flex-col flex">
          <div className="flex flex-col space-y-2">
            <InputText
              label={ t( 'translation:location_name' ) }
              value={ name }
              onChange={ setName }
              textSize="w-5/12 mr-4"
            />
            <div className="min-h-[1.25rem]">
              <p className="text-red-500 text-sm break-words whitespace-pre-wrap">{errors.name ?? ''}</p>
            </div>
          </div>
        </div>
      </BaseModal>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => {return setIsDeleteModalOpen( false );}}
        title={ t( 'translation:delete_location' ) }
        description={ t( 'translation:are_you_sure_that_you_want_to_delete_location_it_is_irreversible' ) }
      />
    </>
  );
};

export default LocationModal;
